import {RefObject, useEffect, useMemo, useState} from 'react';
import useIsLite from './useIsLite';
import useIntersection from '../../common/hook/useIntersection';
import IpVideo, {IpVideoProps} from '../component/IpVideo';
import MotionVideo, {MotionVideoProps} from '../component/MotionVideo';

interface Props
  extends Pick<
      MotionVideoProps,
      | 'motionVideo'
      | 'motionVideoPoster'
      | 'motionVideoAspectRatio'
      | 'motionVideoAnimationMs'
      | 'title'
    >,
    Pick<IpVideoProps, 'eventIpVideo' | 'promotionVideo'> {
  wrapperRef: RefObject<HTMLElement>;
  motionImgRef: RefObject<HTMLPictureElement | HTMLImageElement>;
  timeoutMotionVideoMs?: number;
  timeoutPosterMs?: number;
  onSendViewImp?: () => void;
}

export default function useContentVideo({
  wrapperRef,
  motionImgRef,
  motionVideo,
  motionVideoAnimationMs,
  motionVideoAspectRatio,
  motionVideoPoster,
  title,
  eventIpVideo,
  promotionVideo,
  timeoutMotionVideoMs = 400,
  timeoutPosterMs = 1500,
  onSendViewImp,
}: Props) {
  const isLite = useIsLite();
  const hasMotionVideo = !isLite && !!motionVideo;
  const hasIpVideo = !!eventIpVideo || !!promotionVideo;
  const timeoutMs = hasMotionVideo ? timeoutMotionVideoMs : timeoutPosterMs;

  const [showIpVideo, setShowIpVideo] = useState(false);
  const [isIntersectionWrapper, setIsIntersectionWrapper] = useState(false);
  const [isPlayedStatus, setIsPlayedStatus] = useState({
    motionVideo: !hasMotionVideo,
    ipVideo: false,
  });

  useIntersection(
    {
      targetRef: motionImgRef,
      onIntersection: isIntersecting => {
        if (isIntersecting) {
          setIsPlayedStatus(prev => ({
            ...prev,
            ipVideo: false,
          }));
        }
      },
    },
    !isIntersectionWrapper || (!hasIpVideo && (isLite || !motionVideo))
  );

  useIntersection({
    targetRef: wrapperRef,
    onIntersection: isIntersecting => {
      if (isIntersecting) {
        setIsIntersectionWrapper(true);
        onSendViewImp?.();
      }
    },
  });

  useEffect(() => {
    if (
      isIntersectionWrapper &&
      hasIpVideo &&
      (isPlayedStatus.motionVideo || isLite) &&
      !isPlayedStatus.ipVideo
    ) {
      const timer = window.setTimeout(() => {
        setIsPlayedStatus(prev => ({
          ...prev,
          ipVideo: true,
        }));
        setShowIpVideo(true);
      }, timeoutMs);
      return () => {
        timer && clearTimeout(timer);
      };
    }
  }, [hasIpVideo, isLite, timeoutMs, isPlayedStatus, isIntersectionWrapper]);

  const MotionVideoComponent = useMemo(
    () =>
      ({
        className,
        posterClassName,
        shouldReplay = false,
      }: {
        className?: string;
        posterClassName?: string;
        shouldReplay?: boolean;
      }) => (
        <>
          {hasMotionVideo && (
            <MotionVideo
              shouldReplay={shouldReplay}
              motionVideo={motionVideo}
              motionVideoClassName={className}
              motionVideoPosterClassName={posterClassName}
              motionVideoAnimationMs={motionVideoAnimationMs}
              motionVideoAspectRatio={motionVideoAspectRatio}
              motionVideoPoster={motionVideoPoster}
              title={title}
              onEnded={() => {
                setIsPlayedStatus({
                  motionVideo: true,
                  ipVideo: false,
                });
              }}
            />
          )}
        </>
      ),
    [
      hasMotionVideo,
      motionVideo,
      motionVideoAnimationMs,
      motionVideoAspectRatio,
      motionVideoPoster,
      title,
    ]
  );

  const IpVideoComponent = useMemo(
    () =>
      ({
        className,
        landingUrl,
        onClickIpVideo,
        onClickIpVideoMute,
        onClickIpVideoPlay,
      }: {className?: string} & Pick<
        IpVideoProps,
        | 'landingUrl'
        | 'onClickIpVideo'
        | 'onClickIpVideoMute'
        | 'onClickIpVideoPlay'
      >) => (
        <>
          {hasIpVideo && showIpVideo && (
            <IpVideo
              promotionVideo={promotionVideo}
              eventIpVideo={eventIpVideo}
              eventIpVideoWrapperClassName={className}
              landingUrl={landingUrl}
              onShowIpVideo={setShowIpVideo}
              onClickIpVideo={onClickIpVideo}
              onClickIpVideoMute={onClickIpVideoMute}
              onClickIpVideoPlay={onClickIpVideoPlay}
            />
          )}
        </>
      ),
    [hasIpVideo, showIpVideo, promotionVideo, eventIpVideo]
  );

  return {
    MotionVideoComponent,
    IpVideoComponent,
    showIpVideo,
  };
}
